'use client';

import { Flex } from '@ironhack/design-system2/components/flex';
import { Icon } from '@ironhack/design-system2/components/icon';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { Menu } from '@ironhack/design-system2/icons/menu';
import { type ReactElement, useContext } from 'react';

import { useIsClient } from '@/app/components/client/client-only';
import { IronhackLogoLink } from '@/app/components/client/ironhack-logo-link';
import { RegionSuggestion } from '@/app/components/client/region-suggestion';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { type FullParams, type PageType } from '@/app/urls/url-mapping';
import { type Language } from '@/types';
import { NavFormButtons } from '../nav-form-buttons';
import { EXCEPTION_MEDIA_QUERY } from '../nav-menu';
import { NavbarContext } from '../navbar-context';
import { LanguageSelector } from '../language-selector';
import { type NavbarQuery } from '../types';

type Props = {
  color: 'text.inverse' | 'text.primary' | undefined;
  data: NavbarQuery;
  languageOptions: Partial<Record<Language, string>> | undefined;
  page: PageType;
  params: FullParams;
};

export const NavLogoBar = (props: Props): ReactElement => {
  const { color, data, languageOptions, page, params } = props;
  const { geoRedirectSuggestion } = data;

  const isClient = useIsClient();
  const isHamburgerMenu = useBreakpointValue({ base: true, lg: false });
  const { isFirstLevel, openModalMenu } = useContext(NavbarContext);
  const isDesktopMenuOpen = !isFirstLevel;

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        justify: 'space-between',
        sx: {
          [EXCEPTION_MEDIA_QUERY]: { px: 5 },
        },
      }}
      moduleFormat={{
        background: 'transparent',
        spacingBottom: '16',
        spacingTop: '16',
      }}
      {...(isDesktopMenuOpen && {
        outerProps: {
          bg: [null, null, null, 'brand.secondary'],
        },
      })}
    >
      <RegionSuggestion page={page} texts={geoRedirectSuggestion}>
        <IronhackLogoLink color={color} data={data} />
      </RegionSuggestion>

      <Flex align="center" gap={[4, null, null, 2]}>
        <LanguageSelector
          languageOptions={languageOptions}
          page={page}
          params={params}
        />
        {(!isClient || isHamburgerMenu) && (
          <Icon
            as={Menu}
            boxSize="24px"
            display={[null, null, null, 'none']}
            onClick={openModalMenu}
          />
        )}
        {(!isClient || !isHamburgerMenu) && (
          <NavFormButtons
            data={data}
            display={['none', null, null, 'flex']}
            id="navCTAs"
          />
        )}
      </Flex>
    </ModuleWrapper>
  );
};
